import { useEffect } from "react";
import { Button, Form, Select, message, notification } from "antd";
import { HomeOutlined } from "@ant-design/icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import PageBreadcrumb from "../../components/breadcrumb";
import VendorTransactionFields from "../../components/modals/AddVendorTransactionModal/VendorTransactionFields";
import { useUpdateVendorTransactionMutation } from "../../services/vendor";
import { useGetMerchantsQuery } from "../../services/merchant";
import { useGetBranchesQuery } from "../../services/branches";
import { walletApi } from "../../services/wallet";
import { AllVendorsRoute, ExpenseHistoryRoute, Home } from "../../routes/routepath";
import "../addExpense/styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface EditExpenseFormValues {
  merchant:     string;
  branches:     string[];
  amount:       number;
  paymentType:  'full' | 'partial';
  paidVia:      'wallet' | 'outstanding';
  paymentMode:  'cash' | 'online';
  paidAmount?:  number;
  bill?:        string;
  paymentBill?: string;
  description?: string;
}

// The row data from Expense History is passed via navigation state so this
// page doesn't need a separate "get transaction by id" fetch.
interface LocationState {
  expense?: {
    vendorId?:    string;
    branchIds?:   string[];
    amount?:      number;
    paymentType?: string;
    paidVia?:     string;
    paymentMode?: string;
    paidAmount?:  number;
    bill?:        string;
    paymentBill?: string;
    description?: string;
  };
}

const breadcrumbItems = [
  { label: <HomeOutlined />, to: Home },
  { label: "All Vendors", to: AllVendorsRoute },
  { label: "Edit Expense" },
];

/* ─── Component ──────────────────────────────────────────────────────── */

const EditExpense = () => {
  const [form]   = Form.useForm<EditExpenseFormValues>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id }   = useParams<{ id: string }>();
  const location = useLocation();
  const expense  = (location.state as LocationState | null)?.expense;

  const { data: merchantsResp, isFetching: merchantsLoading } = useGetMerchantsQuery({ status: 'active', limit: 1000 });
  const { data: branchesResp, isFetching: branchesLoading } = useGetBranchesQuery();
  const [updateVendorTransaction, { isLoading: submitting }] = useUpdateVendorTransactionMutation();

  const merchantOptions = ((merchantsResp as any)?.data || [])
    .filter((m: any) => m.isActive !== false)
    .map((m: any) => ({
      value: m._id,
      label: m.name,
    }));

  const branchList = Array.isArray(branchesResp)
    ? branchesResp
    : (Array.isArray((branchesResp as any)?.data) ? (branchesResp as any).data : []);
  const branchOptions = branchList.map((b: any) => ({
    value: b._id || b.id,
    label: b.name,
  }));

  useEffect(() => {
    if (!expense) return;
    form.setFieldsValue({
      merchant:    expense.vendorId,
      branches:    expense.branchIds || [],
      amount:      expense.amount,
      paymentType: (expense.paymentType as 'full' | 'partial') || 'full',
      paidVia:     (expense.paidVia as 'wallet' | 'outstanding') || 'wallet',
      paymentMode: expense.paymentMode as 'cash' | 'online' | undefined,
      paidAmount:  expense.paidAmount,
      bill:        expense.bill,
      paymentBill: expense.paymentBill,
      description: expense.description,
    });
  }, [expense, form]);

  const paymentType = Form.useWatch('paymentType', form);
  const isPartial    = paymentType === 'partial';

  const onFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[]; errors: string[] }[] }) => {
    if (!errorFields?.length) return;
    const labels = errorFields.map(f => {
      const name = Array.isArray(f.name) ? f.name[0] : f.name;
      return String(name).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    });
    notification.error({
      message:     'Required Fields Missing',
      description: [...new Set(labels)].join(', '),
      placement:   'topRight',
      duration:    4,
    });
  };

  const onFinish = async (values: EditExpenseFormValues) => {
    if (!id) return;
    const paidAmount = isPartial ? (values.paidAmount ?? 0) : values.amount;
    const dueAmount  = isPartial ? Math.max(values.amount - paidAmount, 0) : 0;
    const branchFields = values.branches.length === 1
      ? { branchId:  values.branches[0] }
      : { branchIds: values.branches };
    try {
      await updateVendorTransaction({
        id,
        merchantId:  values.merchant,
        amount:      values.amount,
        paymentType: values.paymentType,
        paidVia:     values.paidVia,
        paymentMode: values.paymentMode,
        bill:        values.bill,
        paymentBill: values.paymentMode === 'online' ? values.paymentBill : undefined,
        description: values.description,
        paidAmount,
        dueAmount,
        ...branchFields,
      }).unwrap();
      // message.success('Expense updated successfully');
      dispatch(walletApi.util.invalidateTags(['Wallet']));
      navigate(ExpenseHistoryRoute);
    } catch {
      /* error toast handled globally by badRequestHandler */
    }
  };

  if (!expense) {
    return (
      <div className="add-expense-page">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
          <h2 style={{ margin: 0 }}>Edit Expense</h2>
          <PageBreadcrumb items={breadcrumbItems} />
        </div>
        <p style={{ color: 'var(--muted)' }}>
          No expense data to edit — please open this from the Expense History table.
        </p>
        <Button type="primary" onClick={() => navigate(ExpenseHistoryRoute)}>Back to Expense History</Button>
      </div>
    );
  }

  return (
    <div className="add-expense-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Edit Expense</h2>
        <PageBreadcrumb items={breadcrumbItems} />
      </div>

      <Form form={form} layout="vertical" onFinish={onFinish} onFinishFailed={onFinishFailed} className="add-expense-form">
        <Form.Item name="merchant" label="Merchant" rules={[{ required: true, message: 'Please select a merchant' }]}>
          <Select
            placeholder="Search merchant..."
            loading={merchantsLoading}
            showSearch
            optionFilterProp="label"
            options={merchantOptions}
          />
        </Form.Item>

        <Form.Item name="branches" label="Branch" rules={[{ required: true, message: 'Please select at least one branch' }]}>
          <Select
            mode="multiple"
            placeholder="Select branch(es)"
            loading={branchesLoading}
            optionFilterProp="label"
            options={branchOptions}
          />
        </Form.Item>

        <VendorTransactionFields form={form} />

        <div className="footer-buttons">
          <Button type="default" onClick={() => navigate(ExpenseHistoryRoute)}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" className="save-btn" loading={submitting}>
            {submitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default EditExpense;
